import { type JSX,useEffect, useState } from 'react';

import Info from '@mui/icons-material/Info';
import { Checkbox,FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { TooltipIcon } from './TooltipIcon';

interface EntitiesMultipleSelectProps<TEntity> {
    inputRef?: React.Ref<HTMLInputElement | undefined>;
    onChange?: (newValue: string[]) => void;
    disabled?: boolean;
    title: string;
    entities: TEntity[],
    itemRenderer: (entity: TEntity) => JSX.Element;
    initialValue?: string[];
    useInfoTooltip?: boolean;
    clearDependencies?: unknown[]
}

const entityIdProperty = 'id';
const entityNameProperty = 'name';
const entityDescriptionProperty = 'description';

export function EntitiesMultipleSelect<TEntity>(props: Readonly<EntitiesMultipleSelectProps<TEntity>>) {
    const {
        inputRef,
        onChange,
        disabled,
        entities,
        title,
        itemRenderer,
        initialValue,
        useInfoTooltip = false,
        clearDependencies = []
    } = props;

    const theme = useTheme();
    const [currentValue, setCurrentValue] = useState<string[]>(initialValue ?? []);

    const labelId = title.replaceAll(/\s/g, '').toLowerCase() + '-label';
    const handleChange = (evt: SelectChangeEvent<string[]>) => {
        const newValue = evt.target.value as string[];
        setCurrentValue(newValue);
        onChange?.(newValue);
    };

    useEffect(() => {
        if (initialValue?.length) {
            setCurrentValue(initialValue);
        }
    }, [initialValue]);

    useEffect(() => {
        if (clearDependencies.length) {
            setCurrentValue([]);
        }
    }, clearDependencies);

    return <FormControl>
        <InputLabel id={labelId}>{title}</InputLabel>
        {<Select
            disabled={disabled}
            labelId={labelId}
            multiple={true}
            inputRef={inputRef}
            onChange={handleChange}
            renderValue={(selected) => {
                const selectedEntitiesList = entities.filter(x => {
                    const entityId = String(x[entityIdProperty as keyof TEntity]);
                    return selected.includes(entityId);
                });
                return selectedEntitiesList.map(entity => {
                    return String(entity[entityNameProperty as keyof TEntity]);
                }).join(', ');
            }}
            label={title}
            value={currentValue}
            sx={{ color: 'text.secondary' }}>
            {
                entities.map(
                    (entity) =>  {
                        const entityId = String(entity[entityIdProperty as keyof TEntity]);
                        const entityDescription = String(entity[entityDescriptionProperty as keyof TEntity]);
                        return <MenuItem
                            key={entityId}
                            value={entityId}>
                            <Checkbox checked={currentValue.includes(entityId)} />
                            {itemRenderer(entity)}
                            {useInfoTooltip &&
                                <TooltipIcon
                                    IconComponent={Info}
                                    tooltipTitle={entityDescription}
                                    iconColor='info'
                                    iconInlineStyles={{ marginLeft: '5px', color: theme.palette.primary.accent as string }}
                                />
                            }
                        </MenuItem>;
                    }
                )
            }
        </Select>}
    </FormControl>;
}
